import Button from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import { CheckboxField, ErrorMessage } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { ValueType } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { P300 } from '@atlaskit/theme/colors';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import {
  EntitlementDetails,
  EntitlementGroup,
  EntitlementTermDetails,
  FetchEntitlementsByBillingDateQuery,
  FetchEntitlementsByBillingDateQueryVariables,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  Maybe,
  PaymentMethodType,
  PricingType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getBillEstimate } from '@atlassian/temp-proxies';
import { creditPolicyResponse, txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import { HydraKeys, LinkedEntitlementsInstance, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_CREDIT_MANAGEMENT,
  ENABLE_ORDER_BILL_ESTIMATION,
  ENABLE_ORDER_CLUBBING_IN_OMS_PAYLOAD,
  sentenceCase,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import {
  commonSidebars,
  InfoPanel,
  InvoiceGroupFormField,
  InvoiceGroupSelectItem,
  useFeatureToggle,
} from '@css-xp/base';
import styled from '@emotion/styled';
import defaultTo from 'lodash/defaultTo';
import moment from 'moment';
import { default as React, useEffect, useMemo, useState } from 'react';
import { validateFreeSubscriptions } from '../../../../entitlement-page/src/components/entitlement-page-utils';
import { getOrderIntentPayload } from './manage-entitlement-helper';
import { CoBillData, DiscretionaryEstimate, Estimate, FormState } from './manage-entitlement-v2';
import { PriceDifference } from './review-change/price-difference';
import { POUpdate } from './utils/manage-po-update';

export interface ManageBillingProfileProps {
  entitlement: EntitlementDetails;
  formState: FormState;
  availableInvoiceGroups?: InvoiceGroup[] | never[];
  allAvailableInvoiceGroups?: InvoiceGroup[] | never[];
  linkedEntitlementInvoiceGroupId: Maybe<string> | undefined;
  updateFormState: (data: FormState) => void;
  termPaymentValidation: {
    response?: creditPolicyResponse;
    loading: boolean;
  };
  setTermPaymentValidation: (data: { response?: creditPolicyResponse; loading: boolean }) => void;
  linkedEntitlements: LinkedDestinationEntitlement[];
  isBillingCycleChanged: boolean;
  setGrandTotal: (value: number) => void;
  isAnnualTermedSelected: boolean;
  isAnnualSelected: boolean;
  isDeferredPaymentIGSelectionBlocked: boolean;
  setIsDeferredPaymentIGSelectionBlocked: (value: boolean) => void;
  coBillData: CoBillData;
  setCoBillData: (data: CoBillData) => void;
  checkFinancePolicy: (data: FormState) => Promise<void>;
  estimatedBill: Estimate;
  setEstimatedBill: (data: Estimate) => void;
  onDiscretionaryEstimateChange: (obj: DiscretionaryEstimate) => void;
  creditDetails: EntitlementTermDetails | null | undefined;
}

export enum FinancePolicyReasonCode {
  ERR_POLICY_ORCH_001 = 'ERR_POLICY_ORCH_001',
  ERR_POLICY_ORCH_002 = 'ERR_POLICY_ORCH_002',
  ERR_POLICY_ORCH_003 = 'ERR_POLICY_ORCH_003',
  ERR_POLICY_ORCH_004 = 'ERR_POLICY_ORCH_004',
}

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box; /* do not set this to border-box or it will break the overflow handling */
  color: #333;
  max-height: 300px;
  max-width: 300px;
  padding: 8px 12px;
`;

const ManageBillingProfileComponent: React.FC<ManageBillingProfileProps> = ({
  entitlement,
  formState,
  availableInvoiceGroups,
  allAvailableInvoiceGroups,
  updateFormState,
  linkedEntitlementInvoiceGroupId,
  termPaymentValidation,
  setTermPaymentValidation,
  linkedEntitlements = [],
  isBillingCycleChanged,
  setGrandTotal,
  isAnnualTermedSelected = false,
  isAnnualSelected,
  isDeferredPaymentIGSelectionBlocked,
  setIsDeferredPaymentIGSelectionBlocked,
  coBillData,
  setCoBillData,
  checkFinancePolicy,
  estimatedBill,
  setEstimatedBill,
  onDiscretionaryEstimateChange,
  creditDetails,
}) => {
  const [isEnabled] = useFeatureToggle();
  const isBillEsimationEnabled = isEnabled(ENABLE_ORDER_BILL_ESTIMATION);
  const isOrderClubbingInOMSEnabled = isEnabled(ENABLE_ORDER_CLUBBING_IN_OMS_PAYLOAD);
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const [canUpdatePO, setCanUpdatePO] = React.useState<boolean>(false);
  const [creditBreached, setCreditBreached] = useState<boolean>(false);
  const isStatusPageProduct = isStatuspage(entitlement.productId);
  const graphqlClient = useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const termPaymentValidationResponse = termPaymentValidation.response;

  const validateTermFinancePolicy = (updatedFormState?: FormState | undefined) => {
    // TTC-16423 : Not to initiate finance call for conversions to non PAID offerings .
    if (!isBillingCycleChanged && formState.offering?.pricingType !== PricingType.PAID) return;
    checkFinancePolicy(updatedFormState ? updatedFormState : formState);
  };

  const checkForTermFinancePolicy = (updatedFormState: FormState) => {
    if (!isCreditManagementEnabled) return;
    const paymentMethodType = updatedFormState?.invoiceGroupId?.invoiceGroup?.defaultPaymentMethod?.type;
    if (paymentMethodType === PaymentMethodType.DEFERRED) {
      validateTermFinancePolicy(updatedFormState);
    } else {
      setTermPaymentValidation({
        loading: false,
        response: { status: txaPushPaymentStatus.ELIGIBLE },
      });
      setIsDeferredPaymentIGSelectionBlocked(false);
    }
  };

  React.useEffect(() => {
    const isLinkedEntitlementInvoiceGroupIdAvailable = !entitlement?.billingGroupId && linkedEntitlementInvoiceGroupId;
    setIsDeferredPaymentIGSelectionBlocked(false);
    if (isLinkedEntitlementInvoiceGroupIdAvailable) {
      updateFormState({
        ...formState,
        invoiceGroupId: {
          invoiceGroup: {},
          value: linkedEntitlementInvoiceGroupId as string,
        },
        coBillTimestamp: null,
      });
    }
    if (availableInvoiceGroups && availableInvoiceGroups.length > 0 && isCreditManagementEnabled) {
      const defaultInvoiceGroupId = isLinkedEntitlementInvoiceGroupIdAvailable
        ? linkedEntitlementInvoiceGroupId
        : formState.invoiceGroupId?.value;
      if (defaultInvoiceGroupId) {
        const defaultInvoiceGroup = availableInvoiceGroups.find(
          (invoiceGroup) => invoiceGroup.id === defaultInvoiceGroupId
        );
        if (defaultInvoiceGroup?.defaultPaymentMethod?.type === PaymentMethodType.DEFERRED) {
          validateTermFinancePolicy();
        }
      } else if (availableInvoiceGroups?.[0]?.defaultPaymentMethod?.type === PaymentMethodType.DEFERRED) {
        validateTermFinancePolicy();
      }
    }
  }, [linkedEntitlementInvoiceGroupId]);

  useEffect(() => {
    if (!isCreditManagementEnabled) {
      return;
    }
    setIsDeferredPaymentIGSelectionBlocked(termPaymentValidationResponse?.status === txaPushPaymentStatus.INELIGIBLE);
    setCreditBreached(
      Boolean(termPaymentValidationResponse?.reasonCode === FinancePolicyReasonCode.ERR_POLICY_ORCH_003)
    );
  }, [termPaymentValidationResponse]);

  const isIGSelectionDisabled = (entitlement: EntitlementDetails) => {
    if (validateFreeSubscriptions(entitlement)) return false;
    return isBillingCycleChanged && isOrderClubbingInOMSEnabled ? false : true;
  };

  const shipToPartyId = formState?.invoiceGroupId?.invoiceGroup?.shipToParty?.id as string;

  React.useEffect(() => {
    fetchBillEstimate();

    if (formState.coBillOption) {
      getCoBillData();
    }
  }, [formState?.invoiceGroupId?.value]);

  const fetchBillEstimate = async (coBillTimestamp?: number) => {
    if (isBillEsimationEnabled) {
      try {
        // for billing cycle change, send skipTrial as true always for bill estimate API
        let orderIntentPayload = getOrderIntentPayload({
          entitlement,
          formState,
          linkedEntitlements,
          isBillingCycleChanged,
          coBillTimestamp,
        });
        if (isBillingCycleChanged || isAnnualSelected) {
          orderIntentPayload = {
            ...orderIntentPayload,
            skipTrial: formState.skipTrialEligible ? true : undefined,
            linkedEntitlements: orderIntentPayload?.linkedEntitlements?.map((item, index) => {
              const linkedEntitlementFormState = formState[item.entitlementId as keyof FormState] as FormState;
              const updatedItem = {
                ...item,
              };

              // first item is the current entitlement so ignoring first index as setting true at root level for it
              if (index !== 0) {
                updatedItem.skipTrial = linkedEntitlementFormState?.skipTrialEligible ? true : undefined;
              }

              return updatedItem;
            }) as LinkedEntitlementsInstance[],
          };
        }
        setEstimatedBill({ loading: true });
        const response = await getBillEstimate(orderIntentPayload);
        setEstimatedBill({ response, loading: false });
        onDiscretionaryEstimateChange({
          discretionaryDiscountAmount: defaultTo(response?.discretionaryDiscountAmount, 0),
          loading: false,
        });
      } catch (error) {
        onDiscretionaryEstimateChange({ error, loading: false });
        setEstimatedBill({ error, loading: false });
      }
    }
  };

  const renderPriceDifference = () => {
    return (
      <PriceDifference
        currentEstimatedBill={entitlement?.nextBillEstimate?.total || 0}
        estimatedBill={estimatedBill}
        setGrandTotal={setGrandTotal}
        isBillingCycleChanged={isBillingCycleChanged}
        entitlement={entitlement}
        renderDetailsAccordion={true}
      />
    );
  };

  const coBillLabel = isAnnualTermedSelected ? 'term' : 'bill';

  const getCoBillData = async () => {
    setCoBillData({
      loading: true,
      response: null,
    });

    const { data } = await graphqlClient.query<
      FetchEntitlementsByBillingDateQuery,
      FetchEntitlementsByBillingDateQueryVariables
    >({
      query: commonSidebars.fetchEntitlementsByBillingDate,
      variables: {
        id: formState?.invoiceGroupId?.value as string,
        entitlementId: entitlement.id,
        isTermed: isAnnualTermedSelected,
        isBillingCycleChange: isBillingCycleChanged,
        filterOptions: [{ key: HydraKeys.BILLING_FREQUENCY, value: sentenceCase(formState.billingFrequency) }],
      },
      fetchPolicy: 'cache-first',
    });

    setCoBillData({
      loading: false,
      response: data?.entitlementsGroupByBillingDate?.billingDateGroup as EntitlementGroup[],
    });
  };

  return (
    <div className="billing-profile-view">
      {renderPriceDifference()}
      <h2>Select a billing profile</h2>
      <p>Selecting a different billing profile will change the payment method used for the subscription.</p>

      {creditBreached && (
        <div>
          <InfoPanel
            type={'error'}
            message={
              "The credit limit for the selected billing profile is insufficient. You can select another billing profile or let the customer know that they're unable to proceed with the payment method associated with this billing profile"
            }
          />
        </div>
      )}
      <div>
        {isOrderClubbingInOMSEnabled ? (
          <InvoiceGroupFormField
            isRequired
            isLoading={termPaymentValidation.loading}
            creditDetails={creditDetails}
            isDisabled={isIGSelectionDisabled(entitlement)}
            availableInvoiceGroups={isBillingCycleChanged ? allAvailableInvoiceGroups : availableInvoiceGroups}
            entitlementBillingInvoiceGroupId={formState?.invoiceGroupId?.value}
            fieldLabel="Billing profile"
            onChange={(value: ValueType<InvoiceGroupSelectItem, false> | React.FormEvent<HTMLSelectElement>) => {
              const updatedFormState = {
                ...formState,
                invoiceGroupId: value as InvoiceGroupSelectItem,
                coBillTimestamp: null,
              };
              checkForTermFinancePolicy(updatedFormState);
              updateFormState(updatedFormState);
            }}
          />
        ) : (
          <InvoiceGroupFormField
            isRequired
            isLoading={termPaymentValidation.loading}
            creditDetails={creditDetails}
            isDisabled={isIGSelectionDisabled(entitlement)}
            availableInvoiceGroups={availableInvoiceGroups}
            entitlementBillingInvoiceGroupId={formState?.invoiceGroupId?.value}
            fieldLabel="Billing profile"
            onChange={(value: ValueType<InvoiceGroupSelectItem, false> | React.FormEvent<HTMLSelectElement>) => {
              const updatedFormState = {
                ...formState,
                invoiceGroupId: value as InvoiceGroupSelectItem,
                coBillTimestamp: null,
              };
              checkForTermFinancePolicy(updatedFormState);
              updateFormState(updatedFormState);
            }}
          />
        )}

        {isDeferredPaymentIGSelectionBlocked && (
          <ErrorMessage>This billing profile is not supported - select another billing profile</ErrorMessage>
        )}
        {!canUpdatePO && (
          <Button
            testId="add-po-button"
            onClick={() => setCanUpdatePO(true)}
            iconBefore={<AddIcon label="add" />}
            appearance="link"
            isDisabled={isDeferredPaymentIGSelectionBlocked}
          >
            Update PO number
          </Button>
        )}
        {canUpdatePO && (
          <POUpdate
            setCanUpdatePO={setCanUpdatePO}
            billingGroupId={formState?.invoiceGroupId?.value}
            entitlement={entitlement}
            shipToPartyId={shipToPartyId}
          />
        )}
      </div>
      <div className="info-section">
        <Tooltip
          component={InlineDialog}
          position="bottom-start"
          content="Not all billing profiles are supported for this subscription. Change the plan details to use an existing billing profile or create a new one with a different payment method."
        >
          <InfoIcon size="medium" label="can't-find-billing-profile" primaryColor={P300} />
        </Tooltip>
        <span>{"Can't find the billing profile you're looking for?"}</span>
      </div>
      {!isStatusPageProduct && isBillingCycleChanged && (
        <div className="co-bill-checkbox">
          <CheckboxField name="coBillOption" defaultIsChecked={formState.coBillOption}>
            {({ fieldProps }) => (
              <>
                <Checkbox
                  {...fieldProps}
                  id="co-bill-checkbox"
                  testId="co-bill-checkbox"
                  onChange={(e) => {
                    fieldProps.onChange(e);
                    const updatedState = {
                      ...formState,
                      coBillOption: !formState.coBillOption,
                    };

                    if (formState.coBillOption) {
                      updatedState.coBillTimestamp = null;
                    } else {
                      getCoBillData();
                    }

                    updateFormState(updatedState);
                  }}
                />
              </>
            )}
          </CheckboxField>
          <span>{`Co-${coBillLabel} with existing subscription`}</span>
        </div>
      )}

      {formState.coBillOption && (
        <>
          <h6 className="co-bill-heading">{`Co-${coBillLabel} with existing subscription`}</h6>
          <p className="co-bill-info">Select the subscription and date you want to match with.</p>
          {coBillData?.loading && (
            <div className="co-bill-loader">
              <Spinner testId="co-bill-loader" size="large" />
            </div>
          )}
          {!coBillData?.loading && (
            <commonSidebars.CoBillingCommon
              billingDateGroup={coBillData?.response as EntitlementGroup[]}
              product={entitlement}
              selectedBillingDate={
                formState.coBillTimestamp ? moment.utc(formState.coBillTimestamp).format(NEW_DATE_FORMAT) : undefined
              }
              handleBillDateChange={(e) => {
                const updatedTimestamp = moment.utc(e?.currentTarget?.value).valueOf();
                const updatedFormState = {
                  ...formState,
                  coBillTimestamp: updatedTimestamp,
                };
                updateFormState(updatedFormState);
                isAnnualTermedSelected && fetchBillEstimate(updatedTimestamp);
                checkForTermFinancePolicy(updatedFormState);
              }}
              isTermed={isAnnualTermedSelected}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ManageBillingProfileComponent;
